
import { Options, Vue, prop } from "vue-class-component";
import { PropType } from "vue";
import SavedAddressList from "@/app/ui/views/pickup/components/saved-address-list/index.vue";
import { PickupScheduleController } from "@/app/ui/controllers/PickupScheduleController";
import {
  ApiRequestList,
  ErrorMessageEntities
} from "@/domain/entities/MainApp";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import { PickupCorporateAddress } from "@/domain/entities/PickupSchedule";
import { FlagsPermissionHorde } from "@/feature-flags";
import { ShipmentBooking } from "@/domain/entities/ShipmentBooking";
import {
  PickupRequestMode,
  PickupRequestFormData,
  RecommendationChip,
  TimeSlot,
  SpecialHandling
} from "./types";
import { PickupRequestFormHandler } from "./usePickupRequestForm";
import { getEligibleTimeSlots } from "./pickupDatetimeEligibility";
import TimeSlotDropdown from "./time-slot-dropdown.vue";
import ModalSttPickupList from "../modal-stt-pickup-list/index.vue";
import { AddressFormSuccessPayload } from "../saved-address-list/types";
import ModalAddNewAddress from "../modal-add-new-address/index.vue";
import ModalSttPickupListDhl from "../modal-stt-pickup-list-dhl/index.vue";
import { formatInputNoDecimal } from "@/app/infrastructures/misc/common-library/FormatInputNoDecimal";
import { AccountController } from "@/app/ui/controllers/AccountController";
import SelectSearch from "@/app/ui/components/select-search/index.vue";
import { ClientNamePOSData } from "@/domain/entities/Partner";
import { ClientController } from "@/app/ui/controllers/ClientController";
import debounce from "lodash/debounce";

interface SpecialHandlingOption {
  key: keyof SpecialHandling;
  testId: string;
  label: string;
}

class Props {
  mode = prop<string>({
    default: "blank",
    type: String as PropType<PickupRequestMode>
  });
  isShown = prop<boolean>({ default: false, type: Boolean });
  prefill = prop<Record<string, unknown> | null>({ default: null });
  onClose = prop<Function>({
    default: () => undefined,
    type: Function as PropType<() => void>
  });
}

@Options({
  emits: ["update:isShown", "viewAllStt", "success", "refresh-summary"],
  components: {
    SavedAddressList,
    TimeSlotDropdown,
    ModalSttPickupList,
    ModalAddNewAddress,
    ModalSttPickupListDhl,
    SelectSearch
  },
  watch: {
    async isShown(newVal: boolean) {
      if (newVal) {
        await this.formHandler.initForm(
          this.mode as PickupRequestMode,
          this.prefill
        );
        await this.initializeDhlSttSelection();
      }
    },
    "formHandler.form.koli"() {
      this.formHandler.validateField("koli");
    },
    "formHandler.form.weight"() {
      this.formHandler.validateField("weight");
    },
    "formHandler.selectedClient.clientId"(newVal: number) {
      if(this.isInternalAccount && newVal) {
        this.onAddressListChanged()
      }
    }
  }
})
export default class ModalRequestPickup extends Vue.with(Props) {
  formHandler = new PickupRequestFormHandler();
  isSavedAddressListOpen = false;
  showAddFirstAddress = false;
  showSttListModal = false;
  showModalFailed = false;
  modalErrorMessageEntities = new ErrorMessageEntities();
  clientOptions: any[] = [];
  clientPage = 1;
  clientKeyword = "";
  clientHasMore = true;
  clientSearchLoading = false;
  clientRequestSeq = 0;
  clientPageSize = 20;

  get isInternalAccount(): boolean {
    return AccountController.accountData.account_type === "internal";
  }

  get isAllowedAddAddress() {
    return this.checkValidityToAddAddress()
  }

  get emptyAddress(): PickupCorporateAddress {
    return new PickupCorporateAddress();
  }

  get specialHandlingOptions(): SpecialHandlingOption[] {
    return [
      {
        key: "dangerousGoods",
        testId: "dangerous-goods",
        label: this.$t(
          "pickup.requestModal.specialHandling.dangerousGoods"
        ) as string
      },
      {
        key: "quarantine",
        testId: "quarantine",
        label: this.$t(
          "pickup.requestModal.specialHandling.quarantine"
        ) as string
      },
      {
        key: "woodPacking",
        testId: "wood-packing",
        label: this.$t(
          "pickup.requestModal.specialHandling.woodPacking"
        ) as string
      }
    ];
  }

  get form(): PickupRequestFormData {
    return this.formHandler.form;
  }

  get shipmentLabel(): string {
    const countryCode =
      AccountController.accountData.account_type_detail?.countryCode ||
      AccountController.accountData.countryCode;
    return countryCode?.toLowerCase() === "my" ? "Airway Bill" : "STT";
  }

  get selectedAddress(): PickupCorporateAddress | null {
    return this.formHandler.selectedAddress;
  }

  get recommendationChips() {
    return this.formHandler.recommendationChips;
  }

  get addressPicPhone(): string {
    if (!this.selectedAddress) return "";
    return `• ${this.selectedAddress.pcaPicName} - ${this.selectedAddress.pcaPhoneNumber}`;
  }

  get addressFullAddress(): string {
    if (!this.selectedAddress) return "";
    const district =
      this.selectedAddress.pcaDistrict?.name ||
      this.selectedAddress.pcaDistrictName;
    if (!district) return this.selectedAddress.pcaAddress;
    return this.selectedAddress.pcaAddress.includes(district)
      ? this.selectedAddress.pcaAddress
      : `${this.selectedAddress.pcaAddress}, ${district}`;
  }

  get minDate(): Date {
    return new Date();
  }

  get timeSlots(): TimeSlot[] {
    const date = this.form.date
      ? new Date(this.form.date + "T00:00:00")
      : new Date();
    return getEligibleTimeSlots(date, this.formHandler.datetimeConfig);
  }

  get modalTitle(): string {
    return this.form.mode === "blank"
      ? (this.$t("pickup.requestModal.title.manual") as string)
      : (this.$t("pickup.requestModal.title.requestPickup") as string);
  }

  async mounted(): Promise<void> {
    if (this.isShown) {
      await this.formHandler.initForm(
        this.mode as PickupRequestMode,
        this.prefill
      );
      await this.initializeDhlSttSelection();
    }
  }

  async initializeDhlSttSelection(): Promise<void> {
    if (!this.isDhlWhitelist) return;

    this.checkedSttNos = new Set();
    this.totalEligibleCount = 0;
    this.allItems = [];

    await this.$nextTick();
    const dhlSttPicker = this.$refs.dhlSttPicker as any;
    await dhlSttPicker?.fetchAllPages();
  }

  onDateUpdate(newDate: Date | string): void {
    this.formHandler.onDateUpdate(newDate);
  }

  onTimeSelect(): void {
    this.formHandler.onTimeSelect();
  }

  applyRecoChip(chip: RecommendationChip): void {
    this.formHandler.applyRecoChip(chip);
  }

  formatRecommendationChip(chip: RecommendationChip): string {
    return this.$t("pickup.requestModal.recommendationChip", {
      koli: chip.koli,
      weight: chip.weight
    }) as string;
  }

  chipHasDG(chip: RecommendationChip): boolean {
    return chip.specialHandling?.includes("DG") ?? false;
  }

  formatIntegerInput(value: string): string {
    return value.replace(/[^0-9]/g, "");
  }

  checkValidityToAddAddress() {
    if(this.isInternalAccount) {
      return !!this.formHandler.selectedClient 
    }

    return true
  }

  async openAddressList(): Promise<void> {
    if (!this.selectedAddress) {
      this.showAddFirstAddress = true;
      return;
    }
    await PickupScheduleController.getListPickupCorporateAddress(
      new ApiRequestList({
        clientId: this.clientIdForAddress,
        page: 1,
        limit: 50,
        isTotalData: false,
        selectAddress: false
      })
    );
    this.isSavedAddressListOpen = true;
  }

  closeAddFirstAddress(): void {
    this.showAddFirstAddress = false;
  }

  get clientIdForAddress() {
    return this.formHandler?.selectedClient?.clientId || 0
  }

  async onAddFirstAddressSuccess(): Promise<void> {
    this.showAddFirstAddress = false;

    // 1. Fetch data alamat terbaru
    await PickupScheduleController.getListPickupCorporateAddress(
      new ApiRequestList({
        clientId: this.clientIdForAddress,
        page: 1,
        limit: 50,
        isTotalData: false,
        selectAddress: true
      })
    );

    // 2. Ambil data alamat paling baru (posisi index 0)
    const newlyCreatedAddress =
      PickupScheduleController.listPickupCorporateAddress?.[0];

    if (newlyCreatedAddress) {
      // Set langsung ke selectedAddress via formHandler
      this.formHandler.selectAddress(newlyCreatedAddress);
    } else {
      this.formHandler.refreshSelectedAddress();
    }

    // 3. Bersihkan error & tutup modal list alamat jika sebelumnya kebuka
    this.formHandler.form.errors.address = "";
    this.isSavedAddressListOpen = false;
    this.$emit("refresh-summary");
  }

  onSelectAddress(data: PickupCorporateAddress): void {
    this.formHandler.selectAddress(data);
  }

  async onAddressListChanged(): Promise<void> {
    // 1. Request ulang daftar alamat terbaru
    await PickupScheduleController.getListPickupCorporateAddress(
      new ApiRequestList({
        clientId: this.clientIdForAddress,
        page: 1,
        limit: 50,
        isTotalData: false,
        selectAddress: false
      })
    );

    const addressList =
      PickupScheduleController.listPickupCorporateAddress || [];

    // Jika daftar alamat kosong setelah ada yang dihapus
    if (addressList.length === 0) {
      PickupScheduleController.setSelectedAddressPickupCorporateAddress(
        new PickupCorporateAddress()
      );
      this.formHandler.refreshSelectedAddress();
      return;
    }

    // 2. Cek apakah alamat yang sedang terpilih masih ada di dalam list
    const currentSelectedId = this.selectedAddress?.pcaId;
    const stillExistAddress = addressList.find(
      (a) => a.pcaId === currentSelectedId
    );

    if (stillExistAddress) {
      // Update reference ke data object terbaru
      this.formHandler.selectAddress(stillExistAddress);
    } else {
      // 3. JIKA KEHAPUS: Cari alamat default yang baru, atau fallback ke item pertama [0]
      const newDefaultAddress =
        addressList.find((a) => a.pcaIsDefault) || addressList[0];
      this.formHandler.selectAddress(newDefaultAddress);
    }

    this.$emit("refresh-summary");
  }

  onViewAllStt(): void {
    this.showSttListModal = true;
    this.$emit("viewAllStt", this.form.sttCount);
  }

  // DHL selection tracking state
  checkedSttNos: Set<string> = new Set();
  totalEligibleCount = 0;
  allItems: ShipmentBooking[] = [];

  get isDhlWhitelist(): boolean {
    return FlagsPermissionHorde.dhl_first_mile_client_whitelist.isEnabled();
  }

  get selectedSttItems(): ShipmentBooking[] {
    return this.allItems.filter((item) => this.checkedSttNos.has(item.sttNo));
  }

  get selectedSummary() {
    if (!this.checkedSttNos.size && !this.allItems.length) {
      return {
        awbCount: this.form.sttCount || 0,
        koliCount: parseInt(this.form.koli, 10) || 0,
        weightKg: parseFloat(this.form.weight) || 0
      };
    }
    const checkedItems = this.selectedSttItems;
    const awbCount = checkedItems.length;
    const koliCount = checkedItems.reduce(
      (acc, i) => acc + (i.sttTotalPiece || 1),
      0
    );
    const weightKg = checkedItems.reduce(
      (acc, i) => acc + (i.sttChargeAbleWeight || 0),
      0
    );
    return {
      awbCount,
      koliCount,
      weightKg: Math.round(weightKg * 100) / 100
    };
  }

  onSelectionChange(payload: {
    checkedSttNos: Set<string>;
    totalEligibleCount: number;
    allItems: ShipmentBooking[];
  }): void {
    this.checkedSttNos = payload.checkedSttNos;
    this.totalEligibleCount = payload.totalEligibleCount;
    this.allItems = payload.allItems || [];

    if (this.isDhlWhitelist) {
      const summary = this.selectedSummary;
      this.form.koli = String(summary.koliCount);
      this.form.weight = String(Number(Math.ceil(summary.weightKg)));
      this.form.serviceCodes = Array.from(
        new Set(
          this.selectedSttItems
            .map((item) => item.sttProductType)
            .filter(Boolean)
        )
      );
    }
  }

  get isDhlSttSelected(): boolean {
    return this.isDhlWhitelist && this.checkedSttNos.size > 0;
  }

  get isSubmitDisabled(): boolean {
    if (!this.formHandler.isFormValid || this.formHandler.isSubmitting) {
      return true;
    }
    return false;
  }

  get derivedSttNos(): string[] | undefined {
    if (!this.isDhlWhitelist) {
      return undefined;
    }
    if (!this.checkedSttNos.size) {
      return undefined;
    }
    const isCheckedAll =
      (this.totalEligibleCount > 0 &&
        this.checkedSttNos.size === this.totalEligibleCount) ||
      (this.allItems.length > 0 &&
        this.checkedSttNos.size === this.allItems.length);
    return isCheckedAll ? ["ALL"] : Array.from(this.checkedSttNos);
  }

  async loadClientPage(keyword: string, page: number): Promise<void> {
    const seq = ++this.clientRequestSeq;
    this.clientSearchLoading = true;
    try {
      const res = await ClientController._onGetCLientMappingList({
        search: keyword,
        page,
        limit: this.clientPageSize,
        isBranch: true,
        isParent: false
      });
      if (seq !== this.clientRequestSeq) return;
      if (!res) {
        if (page === 1) {
          this.clientOptions = [];
        }
        return;
      }
      const resData = res.map(item => {
        return {
          ...item,
          clientName: `${item.clientCode} - ${item.clientName}`
        }
      })
      const data = resData.filter(
        (item: any, index: number, items: any[]) =>
          items.findIndex((x) => x.clientId === item.clientId) === index
      );
      if (page === 1) {
        this.clientOptions = data;
      } else {
        const existingIds = this.clientOptions.map((item) => item.clientId);
        this.clientOptions = [
          ...this.clientOptions,
          ...data.filter((item: any) => !existingIds.includes(item.clientId))
        ];
      }
      this.clientPage = page;
      this.clientHasMore = data.length === this.clientPageSize;
    } catch {
      if (seq === this.clientRequestSeq && page === 1) {
        this.clientOptions = [];
      }
    } finally {
      if (seq === this.clientRequestSeq) {
        this.clientSearchLoading = false;
      }
    }
  }

  onClientSearchFocus(): void {
    this.loadClientPage(this.clientKeyword, 1);
  }

  onClientSearchFilter = debounce((keyword: string) => {
    this.clientKeyword = keyword;
    this.clientPage = 1;
    this.loadClientPage(keyword, 1);
  }, 250);

  onClientSearchScrolled(): void {
    if (!this.clientHasMore || this.clientSearchLoading) return;
    this.loadClientPage(this.clientKeyword, this.clientPage + 1);
  }

  async onClientSelect(item: any): Promise<void> {
    if (!item) {
      this.formHandler.clearSelectedClient();
      PickupScheduleController.setSelectedAddressPickupCorporateAddress(
        new PickupCorporateAddress()
      );
      this.formHandler.refreshSelectedAddress();
      return;
    }
    await this.formHandler.selectClient(item);
  }

  async handleSubmit(): Promise<void> {
    this.showModalFailed = false;
    try {
      await this.formHandler.submit(this.derivedSttNos);
      if (this.formHandler.shipmentId) {
        this.$emit("success", this.formHandler.successPayload);
        this.onClose();
      }
    } catch (error) {
      this.modalErrorMessageEntities = parsingErrorResponse(
        error,
        this.$t("pickup.requestModal.error.title") as string,
        this.handleSubmit,
        () => {
          this.showModalFailed = false;
        }
      );
      this.showModalFailed = true;
    }
  }
}
